import type { EditPatientBody } from '../../../../services/patientEdit';

/** UI-facing mirror of the backend's PATIENT_FIELD_REGISTRY - deliberately
 *  duplicated (UI metadata only; the backend Zod schema stays the enforcement).
 *  Grow it in lockstep with the backend registry. */

export type EditPatientFieldKey = keyof EditPatientBody;
export type EditPatientInputType =
    'text' | 'textarea' | 'number' | 'date' | 'select' | 'url' | 'phone';

export interface EditPatientFieldOption {
    value: string;
    label: string;
}

export interface EditPatientFieldConfig {
    key: EditPatientFieldKey;
    label: string;
    group: string;
    inputType: EditPatientInputType;
    required?: boolean;
    placeholder?: string;
    options?: EditPatientFieldOption[];
    /** Span both grid columns instead of sharing a row with the next field. */
    fullWidth?: boolean;
    /** Muted helper line rendered under the field. */
    hint?: string;
}

export const EDIT_PATIENT_FIELDS: EditPatientFieldConfig[] = [
    // --- Identity ---
    {
        key: 'firstName',
        label: 'First name',
        group: 'Identity',
        inputType: 'text',
        required: true,
        placeholder: 'Jane'
    },
    {
        key: 'lastName',
        label: 'Last name',
        group: 'Identity',
        inputType: 'text',
        required: true,
        placeholder: 'Doe'
    },
    {
        key: 'email',
        label: 'Email',
        group: 'Identity',
        inputType: 'text',
        // Not required: Patient.email is nullable, and a required rule here
        // would block saving ANY field for a patient with no stored email.
        placeholder: 'jane@example.com'
    },
    {
        key: 'phone',
        label: 'Phone',
        group: 'Identity',
        inputType: 'phone',
        placeholder: '(201) 555-0123'
    },
    {
        key: 'gender',
        label: 'Gender',
        group: 'Identity',
        inputType: 'select',
        options: [
            { value: '', label: 'Not set' },
            { value: 'Male', label: 'Male' },
            { value: 'Female', label: 'Female' }
        ]
    },
    { key: 'dateOfBirth', label: 'Date of birth', group: 'Identity', inputType: 'date' },

    // --- Treatment ---
    // No "Manufacturer link"/"Manufacturer case ID" here - neither is
    // independently editable (see patient-field-registry.ts).
    {
        key: 'version',
        label: 'Version',
        group: 'Treatment',
        inputType: 'select',
        options: [
            { value: '', label: 'Not set' },
            { value: 'V1', label: 'V1' },
            { value: 'V2', label: 'V2' }
        ]
    },
    {
        key: 'caseType',
        label: 'Case type',
        group: 'Treatment',
        inputType: 'select',
        options: [
            { value: '', label: 'Not set' },
            { value: 'Traditional Ortho', label: 'Traditional Ortho' },
            { value: 'Airway', label: 'Airway' },
            { value: 'Pediatrics', label: 'Pediatrics' },
            { value: 'TMD', label: 'TMD' },
            { value: 'None', label: 'None' }
        ]
    },
    {
        key: 'subCaseType',
        label: 'Sub case type',
        group: 'Treatment',
        inputType: 'select',
        options: [
            { value: '', label: 'Not set' },
            { value: 'Airway 1', label: 'Airway 1' },
            { value: 'Airway 2', label: 'Airway 2' },
            { value: 'Airway 3', label: 'Airway 3' },
            { value: 'TMD 1', label: 'TMD 1' },
            { value: 'TMD 2', label: 'TMD 2' },
            { value: 'TMD 3', label: 'TMD 3' },
            { value: 'Pediatrics 1', label: 'Pediatrics 1' },
            { value: 'Pediatrics 2', label: 'Pediatrics 2' },
            { value: 'Traditional', label: 'Traditional' },
            { value: 'V2 Phase 0 Spring Jet $3500', label: 'V2 Phase 0 Spring Jet $3500' },
            { value: 'V2 Phase 1 Aligners $5100', label: 'V2 Phase 1 Aligners $5100' },
            {
                value: 'V2 Phase 0+1 Expander + Aligners $6500',
                label: 'V2 Phase 0+1 Expander + Aligners $6500'
            },
            {
                value: 'V2 Comprehensive/Adult Mild(never did phase 1) $5200',
                label: 'V2 Comprehensive/Adult Mild (never did phase 1) $5200'
            },
            {
                value: 'V2 Comprehensive/Adult Moderate(never did phase 1) $6100',
                label: 'V2 Comprehensive/Adult Moderate (never did phase 1) $6100'
            },
            {
                value: 'V2 Comprehensive/Adult Complex (never did phase 1) $6850',
                label: 'V2 Comprehensive/Adult Complex (never did phase 1) $6850'
            }
        ]
    },
    {
        key: 'manufacturer',
        label: 'Manufacturer',
        group: 'Treatment',
        inputType: 'select',
        options: [
            { value: '', label: 'Not set' },
            { value: 'Spark Aligners', label: 'Spark Aligners' },
            { value: 'Invisalign Aligners', label: 'Invisalign Aligners' }
        ]
    },
    {
        key: 'alignerOption',
        label: 'Aligner option',
        group: 'Treatment',
        inputType: 'select',
        options: [
            { value: '', label: 'Not set' },
            { value: 'Active', label: 'Active' },
            { value: 'Starter', label: 'Starter' }
        ]
    },
    {
        key: 'refinementsCount',
        label: 'Refinements count',
        group: 'Treatment',
        inputType: 'number'
    },
    {
        key: 'treatmentGoals',
        label: 'Treatment goals',
        group: 'Treatment',
        inputType: 'textarea',
        placeholder: 'What the treatment plan is aiming to achieve',
        fullWidth: true
    },
    {
        // Doctor-submitted during case submission - the design task already
        // surfaces it under this name (DesignPatientInfo.tsx).
        key: 'additionalInfo',
        label: 'Patient specific goals',
        group: 'Treatment',
        inputType: 'textarea',
        fullWidth: true
    },
    { key: 'iteroScanId', label: 'iTero scan ID', group: 'Treatment', inputType: 'text' }
];

export const EDIT_PATIENT_GROUPS: string[] = Array.from(
    new Set(EDIT_PATIENT_FIELDS.map((f) => f.group))
);

export function fieldsInGroup(group: string): EditPatientFieldConfig[] {
    return EDIT_PATIENT_FIELDS.filter((f) => f.group === group);
}
